import { useLocation, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ShoppingCart, Loader2, Lock, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { SEO } from "@/components/seo";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useCart } from "@/lib/cart";
import { apiRequest } from "@/lib/queryClient";

export default function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/checkout/create-session", {
        items: items.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        // Do NOT clear cart here — the user hasn't paid yet.
        // Cart is cleared in checkout-success.tsx after payment is verified.
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Checkout Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Nothing to Checkout</h1>
          <p className="text-muted-foreground mb-6">
            Add some products to your cart first.
          </p>
          <Link href="/">
            <Button data-testid="button-browse-products">Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Checkout" description="Complete your purchase of Sierra Chart templates and indicators." noIndex />
        <Link href="/cart">
          <Button variant="ghost" className="mb-6" data-testid="button-back-to-cart">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Cart
          </Button>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-checkout-title">
          Checkout
        </h1>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Checkout" description="Complete your purchase of Sierra Chart templates and indicators." noIndex />
        <Link href="/cart">
          <Button variant="ghost" className="mb-6" data-testid="button-back-to-cart">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Cart
          </Button>
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-checkout-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <h2 className="font-semibold text-lg mb-4">Sign In to Checkout</h2>
                  <p className="text-muted-foreground mb-6">
                    You need to sign in to your account to place an order.
                  </p>
                  <Link href="/auth">
                    <Button data-testid="button-checkout-sign-in">Sign In</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardContent className="p-6">
                <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
                <div className="space-y-3 mb-4">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex items-start gap-3">
                      {item.product.image && (
                        <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {item.product.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {item.quantity}
                        </p>
                      </div>
                      <span className="text-sm font-medium">
                        ${(Number(item.product.price) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <Separator className="mb-4" />
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold text-primary" data-testid="text-checkout-total">
                    ${totalPrice.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Checkout" description="Complete your purchase of Sierra Chart templates and indicators." noIndex />
      <Link href="/cart">
        <Button variant="ghost" className="mb-6" data-testid="button-back-to-cart">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cart
        </Button>
      </Link>

      <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-checkout-title">
        Checkout
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              <h2 className="font-semibold text-lg mb-4">Your Information</h2>
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Ordering as:</p>
                <p className="text-lg font-medium" data-testid="text-ordering-as">
                  {user.firstName} {user.lastName} ({user.email})
                </p>
              </div>

              <Separator className="mb-4" />

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Lock className="w-4 h-4" />
                <span>Secure payment powered by Stripe</span>
              </div>

              <Button
                type="button"
                size="lg"
                className="w-full"
                disabled={checkoutMutation.isPending}
                onClick={() => checkoutMutation.mutate()}
                data-testid="button-place-order"
              >
                {checkoutMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting to payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay ${totalPrice.toFixed(2)}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardContent className="p-6">
              <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-start gap-3">
                    {item.product.image && (
                      <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.product.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      ${(Number(item.product.price) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <Separator className="mb-4" />
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold text-primary" data-testid="text-checkout-total">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
